import { Button, Popover, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import type { Ref, RefObject } from 'react';
import PpaSplitSearchInput, { PPA_SPLIT_SEARCH_WIDTH } from 'components/molecules/PPA/PpaSplitSearchInput/PpaSplitSearchInput';
import SavedViewStateSelect from 'components/molecules/PricingSimulatorV3/SavedViewStateSelect';
import InlineEditableName from 'components/molecules/PricingSimulatorV3/InlineEditableName';
import type { InlineEditableNameHandle } from 'components/molecules/PricingSimulatorV3/InlineEditableName';
import SimulatorFilterPanel from 'components/molecules/PricingSimulatorV3/SimulatorFilterPanel/SimulatorFilterPanel';
import type {
    SimulatorAppliedFilters,
    SimulatorFilterCategory,
} from 'components/molecules/PricingSimulatorV3/SimulatorFilterPanel/SimulatorFilterPanel';
import type { ScenarioLensSectionController } from 'components/molecules/PricingSimulatorV3/ScenarioLensSection/scenarioLens.types';
import type { SelectedViewFromList } from 'hooks/ppa/ppaViewPrefetch.helpers';
import type { PPAViewDataSource } from 'types/ppaViews';
import type { SimulationFilterEditedAction, SimulationFilterFooterAction, SimulationToolbarAction, SimulationToolbarConfig } from 'types/ppaSimulationMeta';
import type { SimulatorShowOnlyState } from 'components/molecules/PricingSimulatorV3/SimulatorFilterPanel/simulatorFilter.helpers';
import {
    PPA_SIM_ANALYTICS_BTN,
    PPA_SIM_EXPORT_BTN,
    PPA_SIM_FILTER_BTN,
    PPA_SIM_RESET_BTN,
    PPA_SIM_SCENARIO_RENAME_BTN,
    PPA_SIM_SCENARIO_RENAME_CANCEL_BTN,
    PPA_SIM_SCENARIO_RENAME_CONFIRM_BTN,
    PPA_SIM_SCENARIO_RENAME_ERROR,
    PPA_SIM_SCENARIO_RENAME_INPUT,
    PPA_SIM_SCENARIO_TITLE,
    PPA_SIM_SEARCH_INPUT,
    PPA_SIM_SEARCH_SUBMIT,
    PPA_SIM_SIMULATE_BTN,
    PPA_SIM_TOOLBAR,
    PPA_SIM_VIEW_BTN,
} from 'constants/ppaTestIds';
import {
    AnalyticsIconButton,
    DownloadIconButton,
    FilterIconButton,
    ReloadIconButton,
    SimulateIconButton,
} from './simulationToolbarIcons';
import { MoreActionsToolbarControl } from './SimulationToolbarMoreActions';

type MoreActionsConfirmType = 'duplicate' | 'delete';

export type SimulationToolbarScenarioProps = {
    id?: number;
    name: string;
    ppaScopeId?: number | null;
    appliedScope?: Array<{ dimension?: string; attribute_type?: string; attribute_ex_id?: string[] }>;
    isOwner: boolean;
    renameRef: Ref<InlineEditableNameHandle | null>;
    validateName: (name: string) => string | null;
    onNameSave: (name: string) => Promise<void>;
    onRenamingChange: (isRenaming: boolean) => void;
};

export type SimulationToolbarViewProps = {
    isModified: boolean;
    viewId?: number | null;
    dataSource?: PPAViewDataSource;
    lensControllerRef: RefObject<ScenarioLensSectionController | null>;
    onViewListLoadingChange: (isLoading: boolean) => void;
    onSelectedViewChange: (selection: SelectedViewFromList) => void;
    onLoadedViewChange: (loadedViewId: number | null, isCustomView: boolean, viewName?: string) => void;
    onViewLoaded: () => void;
    onUpdateLoadedView: () => Promise<void>;
    onPersistScenarioSnapshot?: () => Promise<void>;
    onRequestUnsavedLeave?: (proceed: () => void) => void;
    onExitViewRequested?: (completeExit: () => void) => void;
    onScenarioExitComplete: () => void;
};

export type SimulationToolbarSearchProps = {
    value: string;
    onChange: (value: string) => void;
    onSubmit?: (override?: string) => void;
};

export type SimulationToolbarFilterProps = {
    isOpen: boolean;
    categories: SimulatorFilterCategory[];
    editedActionTypes?: SimulationFilterEditedAction[];
    appliedCategorySelections?: Record<string, string[]>;
    appliedShowOnly: SimulatorShowOnlyState;
    defaultShowOnly: SimulatorShowOnlyState;
    showOnlyLabels?: { edited: string; impacted: string };
    footerActions?: SimulationFilterFooterAction[];
    isApplyLoading?: boolean;
    onOpenChange: (open: boolean) => void;
    onApply: (filters: SimulatorAppliedFilters) => void | Promise<boolean>;
};

export type SimulationToolbarMoreActionsProps = {
    confirm: MoreActionsConfirmType | null;
    isOpen: boolean;
    isDeleteSubmitting: boolean;
    isDuplicateSubmitting: boolean;
    items: MenuProps['items'];
    buttonRef: Ref<HTMLButtonElement | null>;
    onOpenChange: (open: boolean) => void;
    onButtonClick: () => void;
    onMenuAction: MenuProps['onClick'];
    onDelete: () => void;
    onDuplicate: () => void;
    onCloseConfirm: () => void;
};

export type SimulationToolbarSimulateResetProps = {
    isSimulateDisabled: boolean;
    isResetDisabled: boolean;
    resetTooltip?: string;
    onReset: () => void;
};

export type SimulationToolbarProps = {
    toolbarConfig: SimulationToolbarConfig;
    scenario: SimulationToolbarScenarioProps;
    view: SimulationToolbarViewProps;
    search: SimulationToolbarSearchProps;
    filter: SimulationToolbarFilterProps;
    moreActions: SimulationToolbarMoreActionsProps;
    simulateReset: SimulationToolbarSimulateResetProps;
};

function normalizeActionId(id: string): string {
    return id.trim().toLowerCase().replace(/-/g, '_');
}

const ENABLED_ACTION_IDS = new Set(['view', 'more_actions', 'more', 'more_options']);

// @code-guard: skip-next-function reason="renderToolbarAction is a dispatch table — one case per action id; complexity is structural, not accidental"
function renderToolbarAction(action: SimulationToolbarAction, props: SimulationToolbarProps): JSX.Element | null {
    const normalizedActionId = normalizeActionId(action.id);
    const isDisabled = !ENABLED_ACTION_IDS.has(normalizedActionId);

    switch (normalizedActionId) {
        case 'analytics':
            return (
                <Button
                    key={action.id}
                    className="toolbar-btn !flex !items-center !gap-2 !px-3 !text-blackTitle !opacity-50"
                    icon={<AnalyticsIconButton />}
                    disabled={isDisabled}
                    data-testid={PPA_SIM_ANALYTICS_BTN}
                >
                    {action.label}
                </Button>
            );

        case 'view':
            return (
                <SavedViewStateSelect
                    key={action.id}
                    triggerTestId={PPA_SIM_VIEW_BTN}
                    scenarioId={props.scenario.id}
                    ppaScopeId={props.scenario.ppaScopeId ?? null}
                    appliedScope={props.scenario.appliedScope}
                    isScenarioModified={props.view.isModified}
                    isScenarioOwner={props.scenario.isOwner}
                    getLiveViewConfig={() => props.view.lensControllerRef.current?.getLiveViewConfig() ?? null}
                    onViewListLoadingChange={props.view.onViewListLoadingChange}
                    onSelectedViewChange={props.view.onSelectedViewChange}
                    onLoadedViewChange={props.view.onLoadedViewChange}
                    onViewLoaded={props.view.onViewLoaded}
                    onUpdateLoadedView={props.view.onUpdateLoadedView}
                    onPersistScenarioSnapshot={props.view.onPersistScenarioSnapshot}
                    onRequestUnsavedLeave={props.view.onRequestUnsavedLeave}
                    onExitViewRequested={props.view.onExitViewRequested}
                    onScenarioExitComplete={props.view.onScenarioExitComplete}
                />
            );

        case 'search':
            return (
                <PpaSplitSearchInput
                    key={action.id}
                    value={props.search.value}
                    onChange={props.search.onChange}
                    onSubmit={props.search.onSubmit}
                    width={PPA_SPLIT_SEARCH_WIDTH.TOOLBAR}
                    className="simulation-toolbar-search"
                    ariaLabel="Search scenario"
                    submitAriaLabel="Submit scenario search"
                    inputTestId={PPA_SIM_SEARCH_INPUT}
                    submitTestId={PPA_SIM_SEARCH_SUBMIT}
                />
            );

        case 'filter':
            return (
                <Popover
                    key={action.id}
                    open={props.filter.isOpen}
                    onOpenChange={props.filter.onOpenChange}
                    trigger="click"
                    placement="bottomRight"
                    content={
                        <SimulatorFilterPanel
                            isOpen={props.filter.isOpen}
                            filterCategories={props.filter.categories}
                            editedActionTypes={props.filter.editedActionTypes}
                            scenarioId={props.scenario.id ?? null}
                            viewId={props.view.viewId ?? null}
                            viewDataSource={props.view.dataSource ?? 'view'}
                            appliedScope={props.scenario.appliedScope}
                            appliedCategorySelections={props.filter.appliedCategorySelections}
                            appliedShowOnly={props.filter.appliedShowOnly}
                            defaultShowOnly={props.filter.defaultShowOnly}
                            showOnlyLabels={props.filter.showOnlyLabels}
                            footerActions={props.filter.footerActions ?? []}
                            isApplyLoading={props.filter.isApplyLoading}
                            onApply={async (filters) => {
                                const didApply = await props.filter.onApply(filters);
                                if (didApply !== false) {
                                    props.filter.onOpenChange(false);
                                }
                                return didApply !== false;
                            }}
                        />
                    }
                    showArrow={false}
                    overlayClassName="simulator-filter-popover"
                    overlayStyle={{ zIndex: 1050 }}
                    align={{ offset: [0, 8] }}
                >
                    <Button
                        className={`toolbar-btn gap-2 !w-[81px] !px-3${props.filter.isOpen ? ' !border-purpleFont !text-purpleFont' : ' !text-blackTitle'}`}
                        icon={<FilterIconButton />}
                        data-testid={PPA_SIM_FILTER_BTN}
                    >
                        {action.label}
                    </Button>
                </Popover>
            );

        case 'reset':
        case 'reload':
        case 'refresh': {
            const resetButton = (
                <Button
                    className="toolbar-icon-btn toolbar-reset-btn"
                    icon={<ReloadIconButton />}
                    aria-label={action.label}
                    disabled={isDisabled || props.simulateReset.isResetDisabled}
                    data-testid={PPA_SIM_RESET_BTN}
                    onClick={props.simulateReset.onReset}
                />
            );

            if (!props.simulateReset.resetTooltip) {
                return (
                    <span key={action.id} className="inline-flex">
                        {resetButton}
                    </span>
                );
            }

            return (
                <Tooltip
                    key={action.id}
                    title={props.simulateReset.resetTooltip}
                    placement="top"
                    destroyTooltipOnHide
                    // @code-guard: skip-next-line reason="antd Tooltip color/overlayInnerStyle background — no token for tooltip dark bg"
                    color="rgba(0, 0, 0, 0.85)"
                    // @code-guard: skip-next-line reason="antd Tooltip color/overlayInnerStyle background — no token for tooltip dark bg"
                    overlayInnerStyle={{ borderRadius: 6, background: 'rgba(0, 0, 0, 0.85)' }}
                >
                    <span className="inline-flex">{resetButton}</span>
                </Tooltip>
            );
        }

        case 'download':
            return (
                <Button
                    key={action.id}
                    className="toolbar-icon-btn"
                    icon={<DownloadIconButton />}
                    aria-label={action.label}
                    disabled={isDisabled}
                    data-testid={PPA_SIM_EXPORT_BTN}
                />
            );

        case 'simulate':
            return (
                <Button
                    key={action.id}
                    className="toolbar-btn gap-2 !text-fontColor/40 !opacity-50"
                    icon={<SimulateIconButton disabled={isDisabled || props.simulateReset.isSimulateDisabled} />}
                    disabled={isDisabled || props.simulateReset.isSimulateDisabled}
                    data-testid={PPA_SIM_SIMULATE_BTN}
                >
                    {action.label}
                </Button>
            );

        case 'more_actions':
        case 'more':
        case 'more_options':
            return (
                <MoreActionsToolbarControl
                    key={action.id}
                    action={action}
                    isDisabled={isDisabled}
                    props={props}
                />
            );

        default:
            return (
                <Button key={action.id} className="toolbar-btn !px-3 !text-blackTitle" disabled={isDisabled}>
                    {action.label}
                </Button>
            );
    }
}

const SimulationToolbar = ({ toolbarConfig, ...props }: SimulationToolbarProps) => (
    <div className="flex items-center justify-between gap-4 pb-4 pt-1" data-testid={PPA_SIM_TOOLBAR}>
        {toolbarConfig.showSimulationName ? (
            <InlineEditableName
                ref={props.scenario.renameRef as Ref<InlineEditableNameHandle>}
                key={props.scenario.id ?? 'none'}
                textClassName="text-[18px] font-medium tracking-[0.1px]"
                ariaLabel="Rename scenario"
                value={props.scenario.name}
                editable={props.scenario.isOwner}
                onEditingChange={props.scenario.onRenamingChange}
                validate={props.scenario.validateName}
                onSave={props.scenario.onNameSave}
                titleTestId={PPA_SIM_SCENARIO_TITLE}
                renameBtnTestId={PPA_SIM_SCENARIO_RENAME_BTN}
                inputTestId={PPA_SIM_SCENARIO_RENAME_INPUT}
                confirmBtnTestId={PPA_SIM_SCENARIO_RENAME_CONFIRM_BTN}
                cancelBtnTestId={PPA_SIM_SCENARIO_RENAME_CANCEL_BTN}
                errorTestId={PPA_SIM_SCENARIO_RENAME_ERROR}
            />
        ) : (
            <span className="text-[18px] font-medium tracking-[0.1px] text-blackTransparent88" data-testid={PPA_SIM_SCENARIO_TITLE}>
                {props.scenario.name}
            </span>
        )}
        <div className="flex items-center gap-2">
            {toolbarConfig.actions.map((action) => renderToolbarAction(action, { toolbarConfig, ...props }))}
        </div>
    </div>
);

export default SimulationToolbar;
